import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";

const loadLogoAsBase64 = (): Promise<string> => {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx?.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = () => resolve("");
      img.src = require("../../images/logo-dark2x.png");
    } catch {
      resolve("");
    }
  });
};

const numberToWordsINR = (num: number): string => {
  if (num === 0) return "Zero";
  const ones = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen",
  ];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const convert = (n: number): string => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
    if (n < 1000) return ones[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + convert(n % 100) : "");
    if (n < 100000) return convert(Math.floor(n / 1000)) + " Thousand" + (n % 1000 ? " " + convert(n % 1000) : "");
    if (n < 10000000) return convert(Math.floor(n / 100000)) + " Lakh" + (n % 100000 ? " " + convert(n % 100000) : "");
    return convert(Math.floor(n / 10000000)) + " Crore" + (n % 10000000 ? " " + convert(n % 10000000) : "");
  };
  const rupees = Math.floor(num);
  const paise = Math.round((num - rupees) * 100);
  let result = "Indian Rupee " + convert(rupees);
  if (paise > 0) result += " and " + convert(paise) + " Paise";
  return result + " Only";
};

export const generateInvoicePdf = async (inv: any) => {
  const doc = new jsPDF();
  const fmtAmt = (v: any) =>
    Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const contentWidth = pageWidth - margin * 2;

  const logoBase64 = await loadLogoAsBase64();

  // ========== PAGE BORDER ==========
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.5);
  doc.rect(8, 8, pageWidth - 16, pageHeight - 16);

  // ========== HEADER: Logo + Company Info (left) | TAX INVOICE (right) ==========
  let y = 14;
  const logoW = 38;
  const logoH = 14;
  if (logoBase64) {
    try {
      doc.addImage(logoBase64, "PNG", margin, y - 4, logoW, logoH);
    } catch {}
  }

  const companyX = margin + logoW + 6;
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0, 0, 0);
  doc.text("Procurex Technologies Private", companyX, y);
  doc.text("Limited", companyX, y + 5);
  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(50, 50, 50);
  const companyLines = [
    "H.No 4-49/2, Vattinagulapally",
    "Sy.No 237",
    "Gandipet, Hyderabad Telangana 500019",
    "India",
    "GSTIN 36AAOCP7271P1ZG",
    "8919901380",
    "bharath.devaram@procurextech.com",
    "www.procurextech.com",
  ];
  let companyY = y + 10;
  companyLines.forEach((line) => {
    doc.text(line, companyX, companyY);
    companyY += 3.5;
  });

  // TAX INVOICE on right
  doc.setFontSize(22);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0, 0, 0);
  doc.text("TAX INVOICE", pageWidth - margin, y + 6, { align: "right" });

  y = companyY + 4;

  // ========== INVOICE INFO SECTION ==========
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.rect(margin, y, contentWidth, 22);

  const midX = margin + contentWidth * 0.55;
  doc.line(midX, y, midX, y + 22);

  // Left side: Invoice details
  const infoLabelX = margin + 3;
  const infoValX = margin + 35;
  let iy = y + 5;
  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);

  doc.setFont("helvetica", "normal");
  doc.text("#", infoLabelX, iy);
  doc.setFont("helvetica", "bold");
  doc.text(`: ${inv.invoiceNumber || "NA"}`, infoValX, iy);

  iy += 4.5;
  doc.setFont("helvetica", "normal");
  doc.text("Invoice Date", infoLabelX, iy);
  doc.setFont("helvetica", "bold");
  doc.text(`: ${inv.invoiceDate ? dayjs(inv.invoiceDate).format("DD/MM/YYYY") : "-"}`, infoValX, iy);

  iy += 4.5;
  doc.setFont("helvetica", "normal");
  doc.text("Terms", infoLabelX, iy);
  doc.text(`: ${inv.billingPeriod || "Due on Receipt"}`, infoValX, iy);

  iy += 4.5;
  doc.setFont("helvetica", "normal");
  doc.text("Due Date", infoLabelX, iy);
  doc.setFont("helvetica", "bold");
  doc.text(`: ${inv.dueDate ? dayjs(inv.dueDate).format("DD/MM/YYYY") : "-"}`, infoValX, iy);

  // Right side: Place of Supply / Status
  const rightLabelX = midX + 3;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("Place Of Supply", rightLabelX, y + 5);
  doc.setFont("helvetica", "bold");
  doc.text(`: ${inv.customerState || "Telangana"} `, rightLabelX + 30, y + 5);

  doc.setFont("helvetica", "normal");
  doc.text("Status", rightLabelX, y + 10);
  doc.setFont("helvetica", "bold");
  doc.text(`: ${inv.status || "Draft"}`, rightLabelX + 30, y + 10);

  doc.setFont("helvetica", "normal");
  doc.text("Payment", rightLabelX, y + 15);
  doc.setFont("helvetica", "bold");
  doc.text(`: ${inv.paymentStatus || "Unpaid"}`, rightLabelX + 30, y + 15);

  y += 22;

  // ========== BILL TO SECTION ==========
  doc.setFillColor(230, 230, 230);
  doc.rect(margin, y, contentWidth, 6, "F");
  doc.setDrawColor(0, 0, 0);
  doc.rect(margin, y, contentWidth, 6, "S");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);
  doc.text("Bill To", margin + 3, y + 4.2);

  y += 6;
  const billToLines: string[] = [];
  if (inv.customerCode) billToLines.push(`Customer Code: ${inv.customerCode}`);
  if (inv.customerGstin) billToLines.push(`GSTIN: ${inv.customerGstin}`);
  if (inv.customerPan) billToLines.push(`PAN: ${inv.customerPan}`);
  const cityState = [inv.customerCity, inv.customerState].filter(Boolean).join(", ");
  if (cityState) billToLines.push(cityState + (inv.customerPincode ? ` - ${inv.customerPincode}` : ""));
  else if (inv.customerPincode) billToLines.push(`Pincode: ${inv.customerPincode}`);
  if (inv.customerPhone) billToLines.push(`Phone: ${inv.customerPhone}`);
  if (inv.customerEmail) billToLines.push(inv.customerEmail);
  billToLines.push(`Currency: ${inv.currencyCode || "INR"}`);

  const billToBoxH = 8 + billToLines.length * 4;
  doc.rect(margin, y, contentWidth, billToBoxH, "S");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text(inv.customerName || "-", margin + 3, y + 5);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  let billY = y + 9.5;
  billToLines.forEach((line) => {
    doc.text(line, margin + 3, billY);
    billY += 4;
  });

  y += billToBoxH;

  // ========== LINE ITEMS TABLE ==========
  const lines = inv.invoiceLines || [];
  const allLines = lines.map((l: any, i: number) => {
    const name = l.moduleName || l.description || "-";
    return [
      (i + 1).toString(),
      name,
      (l.quantity || 1).toString(),
      fmtAmt(l.unitPrice),
      fmtAmt(l.lineAmount || l.totalAmount),
    ];
  });

  y += 2;

  autoTable(doc, {
    startY: y,
    head: [["#", "Item & Description", "Qty", "Rate", "Amount"]],
    body: allLines,
    theme: "grid",
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      fontSize: 7.5,
      cellPadding: 2.5,
      lineColor: [0, 0, 0],
      lineWidth: 0.3,
    },
    bodyStyles: {
      fontSize: 7.5,
      cellPadding: 2.5,
      textColor: [0, 0, 0],
      lineColor: [0, 0, 0],
      lineWidth: 0.3,
    },
    columnStyles: {
      0: { cellWidth: 10, halign: "center" },
      1: { cellWidth: 80 },
      2: { cellWidth: 18, halign: "center" },
      3: { halign: "right" },
      4: { halign: "right", fontStyle: "bold" },
    },
    margin: { left: margin, right: margin },
  });

  y = (doc as any).lastAutoTable.finalY;

  // ========== SUMMARY SECTION ==========
  const summaryStartY = y + 2;
  const summaryRightW = 80;
  const summaryLeftW = contentWidth - summaryRightW;
  const summaryRightX = margin + summaryLeftW;

  const summaryRows: [string, string][] = [];
  summaryRows.push(["Sub Total", fmtAmt(inv.subtotalAmount)]);
  if (inv.couponAmount > 0) {
    const couponLabel = inv.couponCode
      ? `Coupon (${inv.couponCode.length > 18 ? inv.couponCode.substring(0, 18) + "..." : inv.couponCode})`
      : "Coupon Discount";
    summaryRows.push([couponLabel, "- " + fmtAmt(inv.couponAmount)]);
  }
  if (inv.manualDiscountAmount > 0)
    summaryRows.push(["Manual Discount", "- " + fmtAmt(inv.manualDiscountAmount)]);
  if (inv.totalDiscountAmount > 0)
    summaryRows.push(["Total Discount", "- " + fmtAmt(inv.totalDiscountAmount)]);
  if (inv.cgstAmount > 0)
    summaryRows.push([`CGST (${inv.cgstRate || 9}%)`, fmtAmt(inv.cgstAmount)]);
  if (inv.sgstAmount > 0)
    summaryRows.push([`SGST (${inv.sgstRate || 9}%)`, fmtAmt(inv.sgstAmount)]);
  if (inv.igstAmount > 0)
    summaryRows.push([`IGST (${inv.igstRate || 18}%)`, fmtAmt(inv.igstAmount)]);
  if (inv.taxAmount > 0 && !inv.cgstAmount && !inv.sgstAmount && !inv.igstAmount)
    summaryRows.push([`Tax (${inv.taxName || ""})`, fmtAmt(inv.taxAmount)]);
  if (inv.advanceAdjusted > 0)
    summaryRows.push(["Advance Adjusted", "- " + fmtAmt(inv.advanceAdjusted)]);

  let sy = summaryStartY;
  const labelColW = 52;
  const valColW = summaryRightW - labelColW;
  doc.setFontSize(7.5);

  summaryRows.forEach((row) => {
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.rect(summaryRightX, sy, labelColW, 5.5, "S");
    doc.rect(summaryRightX + labelColW, sy, valColW, 5.5, "S");
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
    doc.text(row[0], summaryRightX + labelColW - 2, sy + 4, { align: "right" });
    doc.text(row[1], summaryRightX + summaryRightW - 2, sy + 4, { align: "right" });
    sy += 5.5;
  });

  // Total row
  doc.setFillColor(240, 240, 240);
  doc.rect(summaryRightX, sy, labelColW, 6, "FD");
  doc.rect(summaryRightX + labelColW, sy, valColW, 6, "FD");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text("Total", summaryRightX + labelColW - 2, sy + 4.5, { align: "right" });
  doc.text(`Rs.${fmtAmt(inv.totalAmount)}`, summaryRightX + summaryRightW - 2, sy + 4.5, { align: "right" });
  sy += 6;

  // Balance Due row
  doc.setFillColor(240, 240, 240);
  doc.rect(summaryRightX, sy, labelColW, 6, "FD");
  doc.rect(summaryRightX + labelColW, sy, valColW, 6, "FD");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text("Balance Due", summaryRightX + labelColW - 2, sy + 4.5, { align: "right" });
  doc.text(
    `Rs.${fmtAmt(inv.balanceAmount || inv.netPayableAmount)}`,
    summaryRightX + summaryRightW - 2,
    sy + 4.5,
    { align: "right" }
  );
  sy += 6;

  // Left side: Total in Words
  let ly = summaryStartY + 2;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(0, 0, 0);
  doc.text("Total In Words", margin, ly);
  ly += 4;
  doc.setFont("helvetica", "bolditalic");
  doc.setFontSize(7);
  const totalWords = numberToWordsINR(Number(inv.totalAmount || 0));
  const wordLines = doc.splitTextToSize(totalWords, summaryLeftW - 8);
  doc.text(wordLines, margin, ly);
  ly += wordLines.length * 3.5 + 4;

  // Notes
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.text("Notes", margin, ly);
  ly += 3.5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.text(inv.notes || "Thanks for your business.", margin, ly);
  ly += 6;

  // Bank Details
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.text("Payment To Be Made to the Below Bank Account Details:", margin, ly);
  ly += 4;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  const bankLines = [
    "Account Name: Procurex Technologies Private Limited",
    "Bank Name: Indusind Bank",
    "Account Number: 255557895777",
    "IFSC Code: INDB0001801",
  ];
  bankLines.forEach((line) => {
    doc.text(line, margin, ly);
    ly += 3.5;
  });

  // Authorized Signature
  const authSigY = sy + 8;
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.line(summaryRightX + 5, authSigY, summaryRightX + summaryRightW - 2, authSigY);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.text("Authorized Signature", summaryRightX + summaryRightW / 2, authSigY + 4, { align: "center" });

  // ========== TERMS & CONDITIONS ==========
  const termsY = Math.max(ly + 6, authSigY + 12);

  if (termsY + 60 > pageHeight - 15) {
    doc.addPage();
    ly = 14;
  } else {
    ly = termsY;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);
  doc.text("Terms & Conditions", margin, ly);
  ly += 5;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.5);
  const terms = [
    '1.  The payment against this Tax Invoice shall be made in favour of "Procurex Technologies Private Limited" and shall be vide RTGS, NEFT, NACH, IMPS, Account Payee Cheque, DD or credit/debit card.',
    "2.  Delayed interest rate of 18% per annum will be applicable for any delays in payment beyond the terms of payment as mentioned above.",
    "3.  Any request for return and replacement of delivered products or any request for return and refund shall be handled only in accordance with the Return, Replacement and Refund process outlined in the above referenced Sales Order.",
    "4.  Input tax reversal on the credit note issued by Procurex Technologies Private Limited towards any discount including any rate difference shall be done by you in the same month when such credit notes are issued and shall be disclosed in your periodical GST return.",
    "5.  As per the provisions of the Income Tax Act, you shall deposit the deducted TDS with the government authorities within the prescribed time limit without fail. If any loss is incurred by Procurex Technologies Limited due to your non-compliance, you shall be liable to compensate for such loss.",
    "6.  In case of ex-works, Procurex Technologies Private Limited's responsibility ceases once the product(s) is/are available for delivery to you and Procurex Technologies Private Limited will not be responsible for any shortage, loss or damage in transit.",
  ];

  terms.forEach((term) => {
    const termLines = doc.splitTextToSize(term, contentWidth - 4);
    doc.text(termLines, margin, ly);
    ly += termLines.length * 3 + 2;
  });

  // ========== PAGE BORDER & PAGE NUMBER on all pages ==========
  const pageCount = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.rect(8, 8, pageWidth - 16, pageHeight - 16);
    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    doc.setFont("helvetica", "normal");
    doc.text(String(i), pageWidth - margin, pageHeight - 12, { align: "right" });
  }

  doc.save(`Invoice-${inv.invoiceNumber || inv.invoiceId}.pdf`);
};
